import { Namespace, useT } from "@racoon/i18n";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConnectProto } from "@racoon/api";
import {
  useListGroupMembershipsQuery,
  useListOrgGroupsQuery,
  useAddUserToGroupMutation,
  useRemoveUserFromGroupMutation,
} from "~/shared/api/custom-groups/custom-groups.api";
import { type RoleKey } from "~/shared/api/user/user.grpc";
import { ChipsPickerField } from "./chips-picker-field";

const groupTexts = (t: ReturnType<typeof useT>) => ({
  add: t(Namespace.Employees, "create.groups.add"),
  search: t(Namespace.Employees, "create.groups.search"),
  empty: t(Namespace.Employees, "create.groups.empty"),
  noResults: t(Namespace.Employees, "create.groups.noResults"),
  remove: (name: string) => t(Namespace.Employees, "create.groups.remove", { name }),
});

/** Group membership editor: mutations apply immediately, independent of the form's Save. */
export function GroupsField({ user }: { user: ConnectProto.User }) {
  const t = useT();

  const { data: membershipData } = useListGroupMembershipsQuery(
    user.id && user.ownerOrganizationId
      ? { userId: BigInt(user.id), ownerOrganizationId: user.ownerOrganizationId }
      : skipToken,
  );
  // The backend accepts only groups of the user's own org and its descendants.
  const { data: groupsData } = useListOrgGroupsQuery(
    user.ownerOrganizationId
      ? { ownerOrganizationId: user.ownerOrganizationId, includeDescendants: true }
      : skipToken,
  );
  const [addUserToGroup, { isLoading: isAdding }] = useAddUserToGroupMutation();
  const [removeUserFromGroup, { isLoading: isRemoving }] = useRemoveUserFromGroupMutation();

  const memberships = membershipData?.memberships ?? [];
  const groups = groupsData?.groups ?? [];
  const memberGroupIds = new Set(memberships.map((m) => m.customGroupId?.toString()));
  // Another company's group carries that company's name, so look-alike names stay apart.
  const label = (g: (typeof groups)[number]) =>
    g.ownerOrganizationId?.toString() !== user.ownerOrganizationId?.toString() &&
    g.ownerOrganization?.name
      ? `${g.name ?? ""} · ${g.ownerOrganization.name}`
      : (g.name ?? "");
  const groupName = (groupId: bigint | undefined) => {
    const group = groups.find((g) => g.id?.toString() === groupId?.toString());
    return group ? label(group) : groupId?.toString() || "";
  };

  return (
    <ChipsPickerField
      // A chip is a membership: removing one deletes it by its own id.
      chips={memberships.map((m) => ({ id: String(m.id), name: groupName(m.customGroupId) }))}
      options={groups
        .filter((g) => !memberGroupIds.has(g.id?.toString()))
        .map((g) => ({ id: String(g.id), name: label(g) }))}
      onAdd={(groupId) => {
        if (user.id) {
          addUserToGroup({
            userId: BigInt(user.id),
            customGroupId: BigInt(groupId),
            role: "manager",
          });
        }
      }}
      onRemove={(membershipId) => removeUserFromGroup({ id: BigInt(membershipId) })}
      busy={isAdding || isRemoving}
      inputId="user-group-search"
      texts={groupTexts(t)}
    />
  );
}

/**
 * Create: the picked groups are kept by the form and granted once the user exists.
 * The backend puts a new user in the org's group named after the role itself, so
 * that one shows as soon as a role is picked, locked and never offered again.
 */
export function GroupsDraftField(props: {
  ownerOrganizationId: bigint;
  role: RoleKey | "";
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const t = useT();
  const { value, onChange } = props;
  const { data } = useListOrgGroupsQuery({ ownerOrganizationId: props.ownerOrganizationId });
  const groups = data?.groups ?? [];
  const roleGroup = props.role ? groups.find((g) => g.name === props.role) : undefined;
  // Ids are kept as strings: the API delivers them so at runtime (normalizeBigInt).
  const picked = (g: (typeof groups)[number]) => value.includes(String(g.id));
  const toItem = (g: (typeof groups)[number]) => ({ id: String(g.id), name: g.name ?? "" });

  return (
    <ChipsPickerField
      chips={[
        ...(roleGroup ? [{ ...toItem(roleGroup), locked: true }] : []),
        ...groups.filter(picked).map(toItem),
      ]}
      options={groups.filter((g) => g !== roleGroup && !picked(g)).map(toItem)}
      onAdd={(id) => onChange([...value, id])}
      onRemove={(id) => onChange(value.filter((x) => x !== id))}
      inputId="user-group-search"
      texts={groupTexts(t)}
    />
  );
}
